<template>
  <div class="lesson-import-view">
    <h1>Import Lesson</h1>

    <div v-if="errorMessage" class="error-message">
      <p>{{ errorMessage }}</p>
    </div>

    <div v-if="successMessage" class="success-message">
      <p>{{ successMessage }}</p>
    </div>

    <div class="import-form">
      <form @submit.prevent="handleImport">
        <div class="form-group">
          <label for="title">Lesson Title *</label>
          <input 
            type="text" 
            id="title" 
            v-model="title" 
            :placeholder="`e.g., ${getLanguageName(language)} News Article - March 2025`"
            required 
          />
        </div>

        <div class="form-group">
          <label for="sourceType">Source Type *</label>
          <select id="sourceType" v-model="sourceType" required>
            <option value="text">Text Paste</option>
            <option value="file">File Upload</option>
            <option value="url">URL</option>
            <option value="youtube">YouTube (Coming Soon)</option>
          </select>
        </div>

        <!-- Text Paste -->
        <div v-if="sourceType === 'text'" class="form-group">
          <label for="text">Text Content *</label>
          <textarea 
            id="text" 
            v-model="text" 
            rows="15"
            :placeholder="`Paste your ${getLanguageName(language)} text here...`"
            required
          ></textarea>
          <small>Text will be automatically tokenized when imported.</small>
        </div>

        <!-- File Upload -->
        <div v-if="sourceType === 'file'" class="form-group">
          <label for="file">Text File *</label>
          <input 
            type="file" 
            id="file" 
            @change="handleFileSelect" 
            accept=".txt,.md"
            required 
          />
          <small v-if="selectedFile">Selected: {{ selectedFile.name }}</small>
        </div>

        <!-- URL -->
        <div v-if="sourceType === 'url'" class="form-group">
          <label for="sourceUrl">Source URL *</label>
          <input 
            type="url" 
            id="sourceUrl" 
            v-model="sourceUrl" 
            placeholder="https://example.com/article"
            required 
          />
          <small>Note: URL content extraction coming soon. For now, paste the text content below.</small>
          <textarea 
            v-model="text" 
            rows="10"
            placeholder="Paste the text content from the URL..."
            required
            style="margin-top: 10px;"
          ></textarea>
        </div>

        <!-- YouTube (Placeholder) -->
        <div v-if="sourceType === 'youtube'" class="form-group">
          <label for="youtubeUrl">YouTube URL</label>
          <input 
            type="url" 
            id="youtubeUrl" 
            v-model="sourceUrl" 
            placeholder="https://www.youtube.com/watch?v=..."
          />
          <small class="info-message">YouTube transcript extraction coming soon!</small>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="language">Language *</label>
            <select id="language" v-model="language" required>
              <option value="de">German (de)</option>
              <option value="es">Spanish (es)</option>
              <option value="fr">French (fr)</option>
              <option value="it">Italian (it)</option>
            </select>
          </div>

          <div class="form-group">
            <label>
              <input type="checkbox" v-model="generateTTS" />
              Generate TTS Audio (requires Google Cloud TTS setup)
            </label>
          </div>
        </div>

        <div v-if="textPreview && textPreview.length > 0" class="preview-section">
          <h3>Preview</h3>
          <div class="preview-text markdown-content" v-html="renderedPreview"></div>
          <p class="preview-note">
            Reader uses this markdown rendering; TTS uses a plain-text version.
          </p>
          <p class="preview-stats">
            Approximate tokens: ~{{ textPreview ? Math.ceil((textPreview || '').split(/\s+/).filter(w => w.length > 0).length) : 0 }}
          </p>
        </div>

        <div class="form-actions">
          <button 
            type="submit" 
            :disabled="isImporting || !isFormValid" 
            class="btn btn-primary"
          >
            {{ isImporting ? 'Importing...' : 'Import Lesson' }}
          </button>
          <router-link to="/reader" class="btn btn-secondary">Cancel</router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ApiService from '../services/ApiService'
import MarkdownIt from 'markdown-it'

const markdownRenderer = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: false,
  typographer: true
})

export default {
  name: 'LessonImportView',
  data() {
    return {
      title: '',
      text: '',
      sourceType: 'text',
      sourceUrl: '',
      language: 'de',
      selectedFile: null,
      generateTTS: true,
      isLoading: false,
      isImporting: false,
      errorMessage: '',
      successMessage: ''
    }
  },
  computed: {
    textPreview() {
      return this.normalizeMarkdown(this.text || '')
    },
    renderedPreview() {
      const previewText = this.text || ''
      if (!previewText.trim()) return ''
      return markdownRenderer.render(previewText)
    },
    isFormValid() {
      if (!this.title.trim()) return false
      if (this.sourceType === 'text' && !this.text.trim()) return false
      if (this.sourceType === 'file' && !this.selectedFile) return false
      if (this.sourceType === 'url' && (!this.sourceUrl || !this.text.trim())) return false
      if (this.sourceType === 'youtube') return false // Not implemented yet
      return true
    }
  },
  methods: {
    normalizeMarkdown(rawText) {
      if (!rawText) return ''
      let text = rawText.replace(/\r\n/g, '\n')
      text = text.replace(/```[\s\S]*?```/g, '')
      text = text.replace(/^\s{0,3}#{1,6}\s*/gm, '')
      text = text.replace(/^\s{0,3}>\s?/gm, '')
      text = text.replace(/^\s*([-*+]|\d+\.)\s+/gm, '')
      text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
      text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      text = text.replace(/^\s*([-*_]){3,}\s*$/gm, '')
      text = text.replace(/`/g, '')
      text = text.replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, '$1')
      text = text.replace(/\n{3,}/g, '\n\n')
      return text.trim()
    },
    getLanguageName(code) {
      const languages = {
        'es': 'Spanish',
        'de': 'German',
        'fr': 'French',
        'it': 'Italian',
        'pt': 'Portuguese',
        'ru': 'Russian',
        'ja': 'Japanese',
        'zh': 'Chinese',
        'ko': 'Korean'
      }
      return languages[code] || code.toUpperCase()
    },
    async handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      this.selectedFile = file
      this.errorMessage = ''

      // Read file content
      try {
        const fileContent = await this.readFileAsText(file)
        this.text = fileContent
      } catch (error) {
        console.error('Error reading file:', error)
        this.errorMessage = 'Failed to read file. Please try again.'
      }
    },
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsText(file)
      })
    },
    async handleImport() {
      if (!this.isFormValid) {
        this.errorMessage = 'Please fill in all required fields.'
        return
      }

      this.isImporting = true
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const lessonData = {
          title: this.title,
          raw_text: this.text,
          language: this.language,
          source_type: this.sourceType,
          source_url: this.sourceUrl || null
        }

        const response = await ApiService.reader.createLesson(lessonData)
        
        if (response.status === 201 && response.data) {
          const lessonId = response.data.lesson_id
          const tokenCount = response.data.token_count || 0
          this.successMessage = `Lesson imported successfully! (${tokenCount} tokens)`

          // Generate TTS if requested (always try, it's default now)
          // Add delay to ensure session/auth is fully established
          if (this.generateTTS && lessonId) {
            // Wait a bit for session to be established
            await new Promise(resolve => setTimeout(resolve, 500))
            
            try {
              console.log('Generating TTS for lesson:', lessonId)
              
              // Retry mechanism for TTS generation (in case of auth timing issues)
              let ttsResponse = null
              for (let attempt = 0; attempt < 3; attempt++) {
                try {
                  if (attempt > 0) {
                    console.log(`TTS attempt ${attempt + 1}/3`)
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt))
                  }
                  ttsResponse = await ApiService.reader.generateTTS(lessonId)
                  break // Success, exit retry loop
                } catch (err) {
                  console.warn(`TTS attempt ${attempt + 1} failed:`, err.response?.status, err.response?.data)
                  if (err.response?.status === 403 && attempt < 2) {
                    // Auth error, wait longer and retry
                    continue
                  } else {
                    throw err // Non-auth error or last attempt
                  }
                }
              }
              
              console.log('TTS Response:', ttsResponse)
              
              if (ttsResponse && ttsResponse.data && ttsResponse.data.audio_url) {
                this.successMessage += ' TTS audio generated.'
                console.log('TTS audio URL:', ttsResponse.data.audio_url)
              } else if (ttsResponse && ttsResponse.data && ttsResponse.data.error) {
                console.error('TTS generation failed:', ttsResponse.data.error)
                this.successMessage += ` (TTS: ${ttsResponse.data.error})`
              } else {
                console.error('TTS generation failed: No audio URL returned', ttsResponse)
                console.error('TTS response data:', JSON.stringify(ttsResponse?.data, null, 2))
                this.successMessage += ' (TTS generation failed - you can generate it manually from the lesson page)'
              }
            } catch (ttsError) {
              console.error('TTS generation exception:', ttsError)
              console.error('TTS error response:', ttsError.response)
              console.error('TTS error status:', ttsError.response?.status)
              console.error('TTS error data:', ttsError.response?.data)
              const errorMsg = ttsError.response?.data?.error || ttsError.response?.data?.detail || ttsError.message || 'Check API configuration'
              this.successMessage += ` (TTS failed: ${errorMsg} - you can generate it manually from the lesson page)`
            }
          }

          // Redirect back to reader list after a short delay
          if (this.$router) {
            setTimeout(() => {
              this.$router.push({ name: 'Reader' }).catch((err) => {
                console.error('Navigation error:', err)
                // Fallback: try direct path
                this.$router.push('/reader').catch(() => {
                  console.error('Fallback navigation also failed')
                })
              })
            }, 2000)
          }
        } else {
          throw new Error('Invalid response from server')
        }
      } catch (error) {
        console.error('Error importing lesson:', error)
        this.errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to import lesson. Please try again.'
      } finally {
        this.isImporting = false
      }
    }
  }
}
</script>

<style scoped>
.lesson-import-view {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.import-form {
  margin-top: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input[type="text"],
.form-group input[type="url"],
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
}

.form-group textarea {
  resize: vertical;
  min-height: 200px;
}

.form-group small {
  display: block;
  margin-top: 5px;
  color: #666;
  font-size: 0.9em;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group input[type="checkbox"] {
  width: auto;
  margin-right: 8px;
}

.form-group input[type="file"] {
  width: 100%;
  padding: 8px;
}

.preview-section {
  margin: 30px 0;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
}

.preview-section h3 {
  margin-top: 0;
}

.preview-text {
  background: white;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #ddd;
  max-height: 300px;
  overflow-y: auto;
  line-height: 1.6;
}

.preview-stats {
  margin-top: 10px;
  color: #666;
  font-size: 0.9em;
}

.preview-note {
  margin-top: 10px;
  color: #666;
  font-size: 0.9em;
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin: 16px 0 8px;
}

.markdown-content {
  line-height: 1.6;
  white-space: normal;
}

.markdown-content p {
  margin: 8px 0;
}

.markdown-content ul,
.markdown-content ol {
  margin: 8px 0;
  padding-left: 20px;
}

.markdown-content blockquote {
  margin: 12px 0;
  padding: 8px 12px;
  border-left: 3px solid #ccc;
  background: #f5f5f5;
}

.markdown-content code {
  background: #f2f2f2;
  padding: 0 4px;
  border-radius: 3px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.95em;
}

.markdown-content pre {
  background: #f2f2f2;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
}

.markdown-content pre code {
  padding: 0;
  background: transparent;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 30px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  font-size: 14px;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.success-message {
  background-color: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.info-message {
  color: #856404;
  background-color: #fff3cd;
  padding: 8px;
  border-radius: 4px;
  display: block;
  margin-top: 5px;
}
</style>
